import { Link, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { CampaignDetailDrawer } from '@/components/admin/CampaignDetailDrawer';
import { apiJson } from '@/lib/api';
import type {
  AdminDripCatalogEntry,
  AdminDripCatalogResponse,
  AdminDripSummaryResponse,
} from '@/lib/types';

export type DripGoal = 'activation' | 'conversion' | 'retention' | 'growth' | 'prospects';

const CONV_PER_SEND_TOOLTIP =
  'Conversions ÷ sends in the selected range. Conversions are attributed users or trial events; sends are ledger rows — use as directional yield when comparing campaigns, not a strict cohort rate.';

type Props = {
  goal: DripGoal;
};

export function DripGoalSubTab({ goal }: Props) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useSearch({ from: '/admin/drip-campaigns' });
  const range = (search.range ?? '30d') as '7d' | '30d' | '90d' | 'all';
  const campaignId = search.campaign;

  const catalogQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'catalog'],
    queryFn: () => apiJson<AdminDripCatalogResponse>(getToken, '/v1/admin/drip-campaigns/catalog'),
  });

  const summaryQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'summary', range],
    queryFn: () =>
      apiJson<AdminDripSummaryResponse>(getToken, `/v1/admin/drip-campaigns/summary?range=${range}`),
  });

  const campaigns = (catalogQuery.data?.campaigns ?? []).filter((c) => c.goal === goal);
  const summaryById = new Map(summaryQuery.data?.per_campaign.map((r) => [r.id, r]) ?? []);

  const setRange = (r: '7d' | '30d' | '90d' | 'all') => {
    navigate({ to: pathname, search: { ...search, range: r } });
  };

  const openCampaign = (id: string) => {
    navigate({ to: pathname, search: { ...search, campaign: id } });
  };

  const catalogById = new Map<string, AdminDripCatalogEntry>(
    (catalogQuery.data?.campaigns ?? []).map((c) => [c.id, c])
  );
  const drawerEntry = campaignId ? catalogById.get(campaignId) : undefined;

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center gap-3'>
        <label className='text-xs font-semibold text-nova-muted-foreground'>Range</label>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value as '7d' | '30d' | '90d' | 'all')}
          className='px-3 py-2 border border-nova-input rounded-md text-sm'
        >
          <option value='7d'>7 days</option>
          <option value='30d'>30 days</option>
          <option value='90d'>90 days</option>
          <option value='all'>All time</option>
        </select>
      </div>

      {goal === 'prospects' && (
        <div className='rounded-lg border border-nova-primary/30 bg-nova-primary/5 px-4 py-3 text-sm text-nova-foreground'>
          <span className='font-semibold'>Prospect funnel</span> — full metrics live on{' '}
          <Link to='/admin/prospects' className='text-nova-primary font-medium underline-offset-2 hover:underline'>
            Prospects
          </Link>
          .
        </div>
      )}

      {catalogQuery.isLoading || summaryQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin h-10 w-10 border-2 border-nova-primary border-t-transparent rounded-full' />
        </div>
      ) : catalogQuery.isError || summaryQuery.isError ? (
        <p className='text-red-600'>Could not load campaigns.</p>
      ) : (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
          {campaigns.map((c) => {
            const s = summaryById.get(c.id);
            return (
              <div
                key={c.id}
                className='bg-nova-card rounded-lg border border-nova-border shadow-sm p-4 flex flex-col gap-2'
              >
                <div className='flex flex-wrap items-start justify-between gap-2'>
                  <div>
                    <h3 className='text-base font-semibold text-nova-foreground'>{c.name}</h3>
                    <p className='text-xs font-mono text-nova-muted-foreground mt-0.5'>{c.id}</p>
                  </div>
                  <div className='flex flex-wrap gap-1'>
                    {c.channels.map((ch) => (
                      <span key={ch} className='text-[10px] uppercase font-semibold bg-sky-50 text-sky-800 px-2 py-0.5 rounded'>
                        {ch}
                      </span>
                    ))}
                  </div>
                </div>
                {!c.enabled && (
                  <div className='rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900'>
                    Campaign disabled in config
                  </div>
                )}
                {!c.tracking_enabled && (
                  <span
                    className='self-start text-[10px] font-semibold uppercase bg-slate-100 text-slate-700 px-2 py-0.5 rounded'
                    title='Opens not recorded (mailgun tracking off or non-email channel).'
                  >
                    Tracking off
                  </span>
                )}
                <dl className='grid grid-cols-2 gap-2 text-sm mt-1'>
                  <div>
                    <dt className='text-[10px] font-semibold uppercase text-nova-muted-foreground'>Sends ({range})</dt>
                    <dd className='font-semibold tabular-nums'>{s?.sends ?? 0}</dd>
                  </div>
                  <div>
                    <dt className='text-[10px] font-semibold uppercase text-nova-muted-foreground'>Open rate</dt>
                    <dd className='font-semibold tabular-nums'>
                      {!c.tracking_enabled || s?.open_rate == null || !s.sends
                        ? '—'
                        : `${(100 * s.open_rate).toFixed(1)}%`}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-[10px] font-semibold uppercase text-nova-muted-foreground'>Eligible now</dt>
                    <dd className='font-semibold tabular-nums'>{s?.eligible_now ?? '—'}</dd>
                  </div>
                  <div>
                    <dt
                      className='text-[10px] font-semibold uppercase text-nova-muted-foreground cursor-help'
                      title={CONV_PER_SEND_TOOLTIP}
                    >
                      Conversions
                    </dt>
                    <dd>
                      <div className='font-semibold tabular-nums'>{s?.conversions ?? 0}</div>
                      {s && s.sends > 0 && s.conversion_rate != null ? (
                        <div className='text-xs text-nova-muted-foreground tabular-nums mt-0.5' title={CONV_PER_SEND_TOOLTIP}>
                          {(100 * s.conversion_rate).toFixed(1)}% of sends
                        </div>
                      ) : s && s.sends === 0 ? (
                        <div className='text-xs text-nova-muted-foreground mt-0.5'>No sends in range</div>
                      ) : null}
                    </dd>
                  </div>
                </dl>
                {c.beat_keys.length > 0 && (
                  <p className='text-[10px] text-nova-muted-foreground font-mono'>Beat: {c.beat_keys.join(', ')}</p>
                )}
                <button
                  type='button'
                  onClick={() => openCampaign(c.id)}
                  className='mt-2 self-start text-sm font-medium text-nova-primary hover:underline'
                >
                  Details →
                </button>
              </div>
            );
          })}
        </div>
      )}

      {campaignId && (
        <CampaignDetailDrawer campaignId={campaignId} catalogEntry={drawerEntry} range={range} />
      )}
    </div>
  );
}
